import {
  CheckIcon,
  ClipboardDocumentIcon,
  QrCodeIcon,
  XMarkIcon,
} from "@heroicons/react/24/outline";
import React, { useEffect, useState } from "react";
import toast from "react-hot-toast";
import useWithdrawalStore from "../../store/useWithdrawalStore";
import currencyService from "../../services/currencyService";
import { privateApi } from "../../config/api";

const WithdrawModal = ({ isOpen, onClose, allAssets = [] }) => {
  const [selectedCoin, setSelectedCoin] = useState("");
  const [selectedNetwork, setSelectedNetwork] = useState("");
  const [amount, setAmount] = useState("");
  const [destinationAddress, setDestinationAddress] = useState("");
  const [depositAddress, setDepositAddress] = useState("");
  const [qrCodeVisible, setQrCodeVisible] = useState(false);
  const [copied, setCopied] = useState(false);
  const [showConfirmationModal, setShowConfirmationModal] = useState(false);
  const [addressValidation, setAddressValidation] = useState(null);
  const [isValidatingAddress, setIsValidatingAddress] = useState(false);
  const [calculatedFee, setCalculatedFee] = useState(null);
  const [availableBalance, setAvailableBalance] = useState(null);
  const [isLoadingBalance, setIsLoadingBalance] = useState(false);
  const [balanceError, setBalanceError] = useState(null);

  // Dynamic data from backend
  const [availableCurrencies, setAvailableCurrencies] = useState([]);
  const [availableNetworks, setAvailableNetworks] = useState([]);
  const [feesAndLimits, setFeesAndLimits] = useState(null);
  const [dataLoading, setDataLoading] = useState(true);

  // Use withdrawal store
  const {
    networks,
    isLoadingNetworks,
    withdrawalFee,
    coinLimits,
    isLoadingFees,
    isLoadingLimits,
    withdrawalResponse,
    isLoadingWithdrawal,
    error,
    fetchNetworks,
    fetchWithdrawalFee,
    fetchCoinLimits,
    submitWithdrawal,
    getAvailableNetworks,
    getNetworkWithdrawalFee,
    getMaxWithdrawal,
    getMinWithdrawal,
    resetWithdrawalResponse,
    reset: resetStore,
    clearError,
    validateAddress,
    calculateWithdrawalFee,
  } = useWithdrawalStore();

  // Load initial data when modal opens
  useEffect(() => {
    if (isOpen) {
      loadInitialData();
    }
  }, [isOpen]);

  // Load currencies and initial data
  const loadInitialData = async () => {
    try {
      setDataLoading(true);
      const currencies = await currencyService.getAvailableCurrencies();

      // Filter currencies that support withdrawals
      const withdrawalCurrencies = [];
      for (const currency of currencies) {
        const supportsWithdrawals = await currencyService.supportsWithdrawals(
          currency.code
        );
        if (supportsWithdrawals) {
          withdrawalCurrencies.push(currency);
        }
      }

      setAvailableCurrencies(withdrawalCurrencies);

      // Set first currency as default if no coin selected
      if (withdrawalCurrencies.length > 0 && !selectedCoin) {
        setSelectedCoin(withdrawalCurrencies[0].code);
      }
    } catch (error) {
      console.error("Error loading initial data:", error);
      toast.error("Failed to load currency data");
    } finally {
      setDataLoading(false);
    }
  };

  // Load networks and balance when coin changes
  useEffect(() => {
    if (selectedCoin) {
      loadNetworksForCurrency(selectedCoin);
      loadBalanceForCurrency(selectedCoin);
    } else {
      setAvailableBalance(null);
    }
  }, [selectedCoin]);

  // Load networks for selected currency
  const loadNetworksForCurrency = async (currencyCode) => {
    try {
      const networks = await currencyService.getNetworksForCurrency(
        currencyCode
      );
      setAvailableNetworks(networks);

      // Set first network as default
      if (networks.length > 0) {
        setSelectedNetwork(networks[0].code);
      } else {
        setSelectedNetwork("");
      }

      // Reset related fields
      setAddressValidation(null);
      setCalculatedFee(null);
    } catch (error) {
      console.error(`Error loading networks for ${currencyCode}:`, error);
      toast.error("Failed to load networks");
      setAvailableNetworks([]);
      setSelectedNetwork("");
    }
  };

  // Load balance for selected currency
  const loadBalanceForCurrency = async (currencyCode) => {
    try {
      setIsLoadingBalance(true);
      setBalanceError(null);
      
      const response = await privateApi.get('/v1/portfolio-balance/?currency=USDT');
      const balanceData = response.data?.balance || {};
      
      if (balanceData[currencyCode]) {
        const balance = balanceData[currencyCode];
        setAvailableBalance(parseFloat(balance.actual || 0));
      } else {
        setAvailableBalance(0);
      }
    } catch (error) {
      console.error(`Error loading balance for ${currencyCode}:`, error);
      setBalanceError("Failed to load balance");
      setAvailableBalance(null);
    } finally {
      setIsLoadingBalance(false);
    }
  };

  // Reset form when modal is closed
  useEffect(() => {
    if (!isOpen) {
      setSelectedCoin("");
      setSelectedNetwork("");
      setAmount("");
      setDestinationAddress("");
      setDepositAddress("");
      setShowConfirmationModal(false);
      setAvailableBalance(null);
      setBalanceError(null);
      resetStore();
    }
  }, [isOpen, resetStore]);

  // Load fees and limits when coin and network are selected
  useEffect(() => {
    if (selectedCoin && selectedNetwork) {
      loadFeesAndLimits(selectedCoin, selectedNetwork);
    }
  }, [selectedCoin, selectedNetwork]);

  // Load fees and limits for currency/network
  const loadFeesAndLimits = async (currencyCode, networkCode) => {
    try {
      const feesData = await currencyService.getFeesAndLimits(
        currencyCode,
        networkCode
      );
      setFeesAndLimits(feesData);
      console.log("Withdrawal fees and limits loaded:", feesData);
    } catch (error) {
      console.error(
        `Error loading fees and limits for ${currencyCode}/${networkCode}:`,
        error
      );
      setFeesAndLimits(null);
    }
  };

  // Show error toast when error occurs
  useEffect(() => {
    if (error) {
      toast.error(error);
      clearError();
    }
  }, [error, clearError]);

  // Validate address when it changes
  useEffect(() => {
    if (
      destinationAddress &&
      selectedCoin &&
      selectedNetwork &&
      destinationAddress.length > 10
    ) {
      const validateAddr = async () => {
        setIsValidatingAddress(true);
        try {
          const result = await validateAddress(
            selectedCoin,
            destinationAddress,
            selectedNetwork
          );
          setAddressValidation(result);
        } catch (error) {
          console.error("Address validation error:", error);
          setAddressValidation({
            is_valid: false,
            message: "Validation failed",
          });
        } finally {
          setIsValidatingAddress(false);
        }
      };

      const timeoutId = setTimeout(validateAddr, 500); // Debounce
      return () => clearTimeout(timeoutId);
    } else {
      setAddressValidation(null);
    }
  }, [destinationAddress, selectedCoin, selectedNetwork, validateAddress]);

  // Calculate fee when amount changes
  useEffect(() => {
    if (amount && selectedCoin && selectedNetwork && parseFloat(amount) > 0) {
      const calcFee = async () => {
        try {
          const result = await calculateWithdrawalFee(
            selectedCoin,
            selectedNetwork,
            amount
          );
          setCalculatedFee(result);
        } catch (error) {
          console.error("Fee calculation error:", error);
          setCalculatedFee(null);
        }
      };

      const timeoutId = setTimeout(calcFee, 500); // Debounce
      return () => clearTimeout(timeoutId);
    } else {
      setCalculatedFee(null);
    }
  }, [amount, selectedCoin, selectedNetwork, calculateWithdrawalFee]);

  // Handle form submission
  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!selectedCoin || !selectedNetwork || !amount || !destinationAddress) {
      toast.error("Please fill in all fields");
      return;
    }

    // Validate amount against available balance
    const amountValue = parseFloat(amount);
    if (availableBalance !== null && amountValue > availableBalance) {
      toast.error("Amount exceeds available balance");
      return;
    }

    if (amountValue <= 0) {
      toast.error("Amount must be greater than 0");
      return;
    }

    try {
      // Prepare withdrawal request payload
      const withdrawalPayload = {
        amount: parseFloat(amount),
        currency: selectedCoin,
        data: {
          destination: destinationAddress,
          blockchain_currency: selectedNetwork,
        },
      };

      console.log("Withdrawal payload:", withdrawalPayload);

      // Submit withdrawal using store
      const result = await submitWithdrawal(withdrawalPayload);

      if (result.success) {
        // Success - show confirmation modal
        setShowConfirmationModal(true);
        toast.success("Withdrawal request submitted successfully!");
      } else {
        // Error - show error message
        toast.error(`Error: ${result.error}`);
      }
    } catch (error) {
      console.error("Error submitting withdrawal:", error);
      toast.error("Network error: Failed to submit withdrawal request");
    }
  };

  const copyToClipboard = async (text) => {
    try {
      await navigator.clipboard.writeText(text);
      setCopied(true);
      toast.success("Address copied to clipboard");
      setTimeout(() => setCopied(false), 2000);
    } catch (error) {
      toast.error("Failed to copy address");
    }
  };

  const getCurrentNetwork = () => {
    return getAvailableNetworks(selectedCoin).find(
      (network) => network.symbol === selectedNetwork
    );
  };

  // Convert scientific notation to readable decimal format
  const formatFee = (fee) => {
    if (fee === null || fee === undefined) return "0";

    // Convert scientific notation to decimal
    const num = parseFloat(fee);
    if (isNaN(num)) return "0";

    // Format to show appropriate decimal places
    if (num < 0.0001) {
      return num.toFixed(8);
    } else if (num < 1) {
      return num.toFixed(6);
    } else {
      return num.toFixed(4);
    }
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-50 overflow-y-auto">
      <div className="flex items-center justify-center min-h-screen p-4">
        {/* Background overlay */}
        <div
          className="fixed inset-0 bg-black bg-opacity-75 transition-opacity"
          onClick={onClose}
        ></div>

        {/* Modal - Fixed height with scrollbar */}
        <div className="relative bg-gray-800 rounded-xl text-left overflow-hidden shadow-xl transform transition-all w-full max-w-lg mx-auto max-h-[90vh] flex flex-col">
          {/* Header - Fixed height */}
          <div className="flex items-center justify-between p-6 border-b border-gray-700 flex-shrink-0">
            <h3 className="text-lg font-semibold text-white">
              Withdraw {selectedCoin || "Crypto"}
            </h3>
            <button
              onClick={onClose}
              className="text-white hover:text-white transition-colors"
            >
              <XMarkIcon className="h-6 w-6" />
            </button>
          </div>

          {/* Content - Scrollable with fixed height */}
          <div
            className="p-6 space-y-6 overflow-y-auto flex-1"
            style={{ maxHeight: "calc(90vh - 200px)" }}
          >
            <form onSubmit={handleSubmit} className="space-y-6">
              {/* Coin Selection */}
              <div>
                <label className="block text-sm font-medium text-white mb-3">
                  Select Coin
                </label>
                {isLoadingNetworks ? (
                  <div className="w-full p-3 bg-gray-700 border border-gray-600 rounded-lg text-white">
                    Loading available coins...
                  </div>
                ) : availableCurrencies.length > 0 ? (
                  <select
                    value={selectedCoin}
                    onChange={(e) => setSelectedCoin(e.target.value)}
                    className="w-full p-3 bg-gray-700 border border-gray-600 rounded-lg text-white focus:border-blue-500 focus:outline-none"
                    required
                    disabled={dataLoading}
                  >
                    <option value="">
                      {dataLoading ? "Loading coins..." : "Select a coin"}
                    </option>
                    {availableCurrencies.map((currency) => (
                      <option key={currency.code} value={currency.code}>
                        {currency.code}
                      </option>
                    ))}
                  </select>
                ) : (
                  <div className="w-full p-3 bg-gray-700 border border-gray-600 rounded-lg text-red-400">
                    No coins available
                  </div>
                )}
              </div>

              {/* Network Selection */}
              {selectedCoin && (
                <div>
                  <label className="block text-sm font-medium text-white mb-3">
                    Select Network
                  </label>
                  {dataLoading ? (
                    <div className="w-full p-3 bg-gray-700 border border-gray-600 rounded-lg text-white">
                      Loading available networks...
                    </div>
                  ) : (
                    <select
                      value={selectedNetwork}
                      onChange={(e) => setSelectedNetwork(e.target.value)}
                      className="w-full p-3 bg-gray-700 border border-gray-600 rounded-lg text-white focus:border-blue-500 focus:outline-none"
                      required
                      disabled={dataLoading}
                    >
                      <option value="">
                        {dataLoading
                          ? "Loading networks..."
                          : "Select a network"}
                      </option>
                      {availableNetworks.map((network) => (
                        <option key={network.code} value={network.code}>
                          {network.name === "BitcoinV1" ? "BTC1" : network.name}
                        </option>
                      ))}
                    </select>
                  )}
                  {dataLoading && (
                    <div className="text-xs text-white mt-1">
                      Loading available networks...
                    </div>
                  )}
                  {!dataLoading &&
                    availableNetworks.length === 0 &&
                    selectedCoin && (
                      <div className="text-xs text-white mt-1">
                        Using {selectedCoin} as the default network
                      </div>
                    )}
                </div>
              )}

              {/* Amount Input */}
              <div>
                <label className="block text-sm font-medium text-white mb-3">
                  Amount
                </label>
                <input
                  type="text"
                  value={amount}
                  onChange={(e) => {
                    // Only allow numbers, decimals, and basic math operations
                    const value = e.target.value;
                    if (value === "" || /^[0-9]*\.?[0-9]*$/.test(value)) {
                      console.log("Amount changed:", value);
                      setAmount(value);
                    }
                  }}
                  placeholder="Enter amount"
                  className={`w-full p-3 bg-gray-700 border rounded-lg text-white focus:outline-none [appearance:textfield] [&::-webkit-outer-spin-button]:appearance-none [&::-webkit-inner-spin-button]:appearance-none ${
                    availableBalance !== null &&
                    amount &&
                    parseFloat(amount) > availableBalance
                      ? "border-red-500 focus:border-red-400"
                      : "border-gray-600 focus:border-blue-500"
                  }`}
                  required
                />
                {/* Balance Display */}
                {selectedCoin && (
                  <div className="mt-2">
                    {isLoadingBalance ? (
                      <div className="text-xs text-white">
                        Loading balance...
                      </div>
                    ) : balanceError ? (
                      <div className="text-xs text-red-400">{balanceError}</div>
                    ) : availableBalance !== null ? (
                      <div className="flex items-center justify-between">
                        <span className="text-xs text-white">
                          Available Balance:
                        </span>
                        <span className="text-xs text-white font-medium">
                          {formatFee(availableBalance)} {selectedCoin}
                        </span>
                      </div>
                    ) : null}
                    {/* Error message if amount exceeds balance */}
                    {availableBalance !== null &&
                      amount &&
                      parseFloat(amount) > availableBalance && (
                        <div className="mt-1 text-xs text-red-400">
                          Amount exceeds available balance
                        </div>
                      )}
                  </div>
                )}
              </div>

              {/* Destination Address Input */}
              <div>
                <label className="block text-sm font-medium text-white mb-3">
                  Destination Address
                </label>
                <div className="relative">
                  <input
                    type="text"
                    value={destinationAddress}
                    onChange={(e) => {
                      console.log("Address changed:", e.target.value);
                      setDestinationAddress(e.target.value);
                    }}
                    placeholder="Enter destination address"
                    className={`w-full p-3 bg-gray-700 border rounded-lg text-white focus:outline-none ${
                      addressValidation === null || isValidatingAddress
                        ? "border-gray-600 focus:border-blue-500"
                        : addressValidation.is_valid
                        ? "border-green-500 focus:border-green-400"
                        : "border-red-500 focus:border-red-400"
                    }`}
                    required
                  />
                  {isValidatingAddress && (
                    <div className="absolute right-3 top-3">
                      <svg
                        className="animate-spin h-5 w-5 text-white"
                        xmlns="http://www.w3.org/2000/svg"
                        fill="none"
                        viewBox="0 0 24 24"
                      >
                        <circle
                          className="opacity-25"
                          cx="12"
                          cy="12"
                          r="10"
                          stroke="currentColor"
                          strokeWidth="4"
                        ></circle>
                        <path
                          className="opacity-75"
                          fill="currentColor"
                          d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
                        ></path>
                      </svg>
                    </div>
                  )}
                </div>
                {addressValidation && !isValidatingAddress && (
                  <div
                    className={`mt-2 text-sm ${
                      addressValidation.is_valid
                        ? "text-white"
                        : "text-red-400"
                    }`}
                  >
                    {addressValidation.message}
                  </div>
                )}
              </div>

              {/* Submit Button */}
              <button
                type="submit"
                disabled={
                  isLoadingWithdrawal ||
                  !selectedCoin ||
                  !selectedNetwork ||
                  !amount ||
                  !destinationAddress ||
                  getAvailableNetworks(selectedCoin).length === 0 ||
                  (availableBalance !== null &&
                    parseFloat(amount) > availableBalance) ||
                  parseFloat(amount) <= 0
                }
                className="w-full py-3 px-4 bg-[#0E1696] hover:bg-blue-700 disabled:bg-gray-600 disabled:cursor-not-allowed rounded-lg !text-white font-medium transition-colors flex items-center justify-center"
              >
                {isLoadingWithdrawal ? (
                  <>
                    <svg
                      className="animate-spin -ml-1 mr-3 h-5 w-5 text-white"
                      xmlns="http://www.w3.org/2000/svg"
                      fill="none"
                      viewBox="0 0 24 24"
                    >
                      <circle
                        className="opacity-25"
                        cx="12"
                        cy="12"
                        r="10"
                        stroke="currentColor"
                        strokeWidth="4"
                      ></circle>
                      <path
                        className="opacity-75"
                        fill="currentColor"
                        d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
                      ></path>
                    </svg>
                    Processing...
                  </>
                ) : (
                  "Submit Withdraw"
                )}
              </button>
            </form>

            {/* Limits and Fees Display */}
            {selectedCoin && selectedNetwork && feesAndLimits && (
              <div className="space-y-4">
                {/* Withdrawal Limits */}
                <div>
                  <h4 className="text-sm font-medium text-white mb-3">
                    Withdrawal Limits & Fees
                  </h4>
                  <div className="bg-gray-700 rounded-lg p-4">
                    <div className="grid grid-cols-2 gap-4 text-sm">
                      <div>
                        <div className="text-white">Min Withdrawal</div>
                        <div className="text-white font-medium">
                          {currencyService.formatFee(
                            currencyService.getWithdrawalLimits(feesAndLimits)
                              .min
                          )}{" "}
                          {selectedCoin}
                        </div>
                      </div>
                      <div>
                        <div className="text-white">Max Withdrawal</div>
                        <div className="text-white font-medium">
                          {currencyService.formatFee(
                            currencyService.getWithdrawalLimits(feesAndLimits)
                              .max
                          )}{" "}
                          {selectedCoin}
                        </div>
                      </div>
                      <div>
                        <div className="text-white">Network Fee</div>
                        <div className="text-white font-medium">
                          {currencyService.getNetworkWithdrawalFee(
                            feesAndLimits,
                            selectedNetwork,
                            selectedCoin
                          ) !== null
                            ? `${currencyService.formatFee(
                                currencyService.getNetworkWithdrawalFee(
                                  feesAndLimits,
                                  selectedNetwork,
                                  selectedCoin
                                )
                              )} ${selectedCoin}`
                            : "N/A"}
                        </div>
                      </div>
                      <div>
                        <div className="text-white">Confirmations</div>
                        <div className="text-white font-medium">
                          {currencyService.getNetworkConfirmations(
                            selectedNetwork
                          )}
                        </div>
                      </div>
                    </div>

                    {/* Additional Fee Information */}
                    {feesAndLimits?.limitsData?.fee && (
                      <div className="mt-4 pt-4 border-t border-gray-600">
                        <div className="text-xs text-white mb-2">
                          Trading Fees
                        </div>
                        <div className="grid grid-cols-2 gap-4 text-xs">
                          <div>
                            <span className="text-white">Limit Order: </span>
                            <span className="text-white">
                              {(
                                feesAndLimits.limitsData.fee.order?.limit *
                                  100 || 0
                              ).toFixed(3)}
                              %
                            </span>
                          </div>
                          <div>
                            <span className="text-white">
                              Market Order:{" "}
                            </span>
                            <span className="text-white">
                              {(
                                feesAndLimits.limitsData.fee.order?.market *
                                  100 || 0
                              ).toFixed(3)}
                              %
                            </span>
                          </div>
                        </div>
                      </div>
                    )}
                  </div>
                </div>
              </div>
            )}

            {/* WithdrawAddress Section (if available) */}
            {depositAddress && (
              <div>
                <label className="block text-sm font-medium text-white mb-3">
                  Your Withdraw Address
                </label>
                <div className="bg-gray-700 rounded-lg p-4">
                  <div className="flex items-center justify-between mb-3">
                    <span className="text-xs text-white">
                      {getCurrentNetwork()?.name === "BitcoinV1"
                        ? "BTC1"
                        : getCurrentNetwork()?.name}{" "}
                      Network
                    </span>
                    <button
                      onClick={() => setQrCodeVisible(!qrCodeVisible)}
                      className="text-white hover:text-blue-300 text-xs transition-colors"
                    >
                      {qrCodeVisible ? "Hide QR" : "Show QR"}
                    </button>
                  </div>

                  <div className="break-all text-white text-sm mb-3 font-mono bg-gray-800 p-3 rounded">
                    {depositAddress}
                  </div>

                  <button
                    onClick={() => copyToClipboard(depositAddress)}
                    className="w-full flex items-center justify-center py-2 px-4 bg-[#0E1696] hover:bg-blue-700 rounded-lg transition-colors"
                  >
                    {copied ? (
                      <CheckIcon className="h-4 w-4 mr-2" />
                    ) : (
                      <ClipboardDocumentIcon className="h-4 w-4 mr-2" />
                    )}
                    {copied ? "Copied!" : "Copy Address"}
                  </button>

                  {/* QR Code Placeholder */}
                  {qrCodeVisible && (
                    <div className="mt-4 flex justify-center">
                      <div className="w-48 h-48 bg-white rounded-lg flex items-center justify-center">
                        <QrCodeIcon className="h-32 w-32 text-white" />
                      </div>
                    </div>
                  )}
                </div>
              </div>
            )}

            {/* Network Details */}
            {getCurrentNetwork() && coinLimits && (
              <div className="grid grid-cols-2 gap-4 text-sm">
                <div>
                  <div className="text-white">Network</div>
                  <div className="text-white font-medium">
                    {getCurrentNetwork().name === "BitcoinV1"
                      ? "BTC1"
                      : getCurrentNetwork().name}
                  </div>
                </div>

                <div>
                  <div className="text-white">Network Fee</div>
                  <div className="text-white font-medium">
                    {calculatedFee
                      ? `${formatFee(calculatedFee.fee)} ${
                          calculatedFee.fee_currency
                        }`
                      : getNetworkWithdrawalFee(
                          selectedCoin,
                          selectedNetwork
                        ) !== null
                      ? `${formatFee(
                          getNetworkWithdrawalFee(selectedCoin, selectedNetwork)
                        )} ${selectedCoin}`
                      : "N/A"}
                  </div>
                </div>
                <div>
                  <div className="text-white">Min Withdrawal</div>
                  <div className="text-white font-medium">
                    {getMinWithdrawal()} {selectedCoin}
                  </div>
                </div>
                <div>
                  <div className="text-white">Max Withdrawal</div>
                  <div className="text-white font-medium">
                    {getMaxWithdrawal()} {selectedCoin}
                  </div>
                </div>
                {calculatedFee && (
                  <div className="col-span-2">
                    <div className="text-white">You will receive</div>
                    <div className="text-white font-medium text-lg">
                      {formatFee(calculatedFee.receivable_amount)}{" "}
                      {selectedCoin}
                    </div>
                  </div>
                )}
              </div>
            )}
          </div>

          {/* Footer - Fixed height */}
          <div className="px-6 py-4 bg-gray-750 border-t border-gray-700 flex-shrink-0">
            <div className="flex justify-between items-center text-xs text-white">
              <span>Processing time: 5-30 minutes</span>
              <span>24/7 Support Available</span>
            </div>
          </div>
        </div>
      </div>

      {/* Confirmation Modal */}
      {showConfirmationModal && withdrawalResponse && (
        <div className="fixed inset-0 z-60 overflow-y-auto">
          <div className="flex items-center justify-center min-h-screen p-4">
            {/* Background overlay */}
            <div
              className="fixed inset-0 bg-black bg-opacity-75 transition-opacity"
              onClick={() => setShowConfirmationModal(false)}
            ></div>

            {/* Confirmation Modal */}
            <div className="relative bg-gray-800 rounded-xl text-left overflow-hidden shadow-xl transform transition-all w-full max-w-lg mx-auto">
              {/* Header */}
              <div className="flex items-center justify-between p-6 border-b border-gray-700">
                <div className="flex items-center">
                  <div className="flex-shrink-0">
                    <div className="w-10 h-10 bg-green-100 rounded-full flex items-center justify-center">
                      <svg
                        className="w-6 h-6 text-green-600"
                        fill="none"
                        stroke="currentColor"
                        viewBox="0 0 24 24"
                      >
                        <path
                          strokeLinecap="round"
                          strokeLinejoin="round"
                          strokeWidth="2"
                          d="M5 13l4 4L19 7"
                        ></path>
                      </svg>
                    </div>
                  </div>
                  <div className="ml-4">
                    <h3 className="text-lg font-semibold text-white">
                      Withdrawal Successful!
                    </h3>
                    <p className="text-sm text-white">
                      Your withdrawal request has been submitted
                    </p>
                  </div>
                </div>
                <button
                  onClick={() => setShowConfirmationModal(false)}
                  className="text-white hover:text-white transition-colors"
                >
                  <XMarkIcon className="h-6 w-6" />
                </button>
              </div>

              {/* Content */}
              <div className="p-6 space-y-4">
                <div className="bg-gray-700 rounded-lg p-4">
                  <h4 className="text-sm font-medium text-white mb-3">
                    Transaction Details
                  </h4>
                  <div className="space-y-2 text-sm">
                    <div className="flex justify-between">
                      <span className="text-white">Transaction ID:</span>
                      <span className="text-white font-mono">
                        #{withdrawalResponse.id}
                      </span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-white">Amount:</span>
                      <span className="text-white">
                        {withdrawalResponse.amount}{" "}
                        {withdrawalResponse.currency}
                      </span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-white">Network:</span>
                      <span className="text-white">
                        {withdrawalResponse.data?.blockchain_currency}
                      </span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-white">Destination:</span>
                      <span className="text-white font-mono text-xs truncate max-w-[200px]">
                        {withdrawalResponse.data?.destination}
                      </span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-white">Status:</span>
                      <span className="text-white">Pending</span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-white">Created:</span>
                      <span className="text-white text-xs">
                        {withdrawalResponse.created}
                      </span>
                    </div>
                  </div>
                </div>

                <div className="bg-blue-900 bg-opacity-20 border border-blue-700 rounded-lg p-4">
                  <div className="flex items-start">
                    <div className="flex-shrink-0">
                      <svg
                        className="w-5 h-5 text-white mt-0.5"
                        fill="none"
                        stroke="currentColor"
                        viewBox="0 0 24 24"
                      >
                        <path
                          strokeLinecap="round"
                          strokeLinejoin="round"
                          strokeWidth="2"
                          d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z"
                        ></path>
                      </svg>
                    </div>
                    <div className="ml-3">
                      <p className="text-sm text-blue-300">
                        Your withdrawal request is being processed. You will
                        receive a confirmation once the transaction is completed
                        on the blockchain.
                      </p>
                    </div>
                  </div>
                </div>
              </div>

              {/* Footer */}
              <div className="px-6 py-4 bg-gray-750 border-t border-gray-700">
                <button
                  onClick={() => {
                    setShowConfirmationModal(false);
                    onClose(); // Close the main withdrawal modal
                  }}
                  className="w-full py-2 px-4 bg-[#0E1696] !text-white hover:bg-blue-700 rounded-lg font-medium transition-colors"
                >
                  Close
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default WithdrawModal;
